import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { clsx } from "clsx";
import dayjs from "dayjs";

import AutoComplete from "components/AutoComplete";
import Button from "components/Button";
import DatePicker from "components/DatePicker";
import Select from "components/Select";
import { EQUIPMENT_TYPES } from "constants/signup";
import { FILTER_CARRIER_LIST, REGION_DATA } from "constants/trucks";
import useCurrentLocation from "hooks/useCurrentLocation";
import useDeviceType from "hooks/useDeviceType";
import { TruckFilterFormData } from "models/Truck";
import styles from "./style.module.scss";
import { showErrorMessage } from "helpers/toastMessage";
import Icon from "@mdi/react";
import { mdiClose } from "@mdi/js";

interface ITruckFilterFormProps {
  loading?: boolean;
  setIsScroll: React.Dispatch<React.SetStateAction<boolean>>;
  setFilter: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  toggleFilterModal: VoidFunction;
  resetPagination: VoidFunction;
}

export default function TruckFilterForm({
  setIsScroll,
  setFilter,
  toggleFilterModal,
  resetPagination
}: Readonly<ITruckFilterFormProps>) {
  const { isDesktop } = useDeviceType();
  const { refreshLocation } = useCurrentLocation();
  const [isDateRange, setIsDateRange] = useState(false);
  const [prevFilters, setPrevFilters] = useState<TruckFilterFormData>();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { isLoading, isSubmitted }
  } = useForm<Partial<TruckFilterFormData>>();

  function onReverseSearch() {
    const pickupState = getValues("pickUpState") ?? [];
    const deliveryState = getValues("deliveryState") ?? [];
    setValue("pickUpState", deliveryState);
    setValue("deliveryState", pickupState);
  }

  const getCurrentState = async () => {
    try {
      const { currentState } = await refreshLocation();
      if (currentState) {
        const state = REGION_DATA.find((region) => region.label === currentState);
        state && setValue("pickUpState", [state]);
      }
    } catch {
      showErrorMessage("Error getting location");
    }
  };

  const onSubmit = (truckFilterFormData: TruckFilterFormData) => {
    if (prevFilters && JSON.stringify(prevFilters) === JSON.stringify(truckFilterFormData)) {
      return;
    }
    const { pickUpState, deliveryState, equipmentType, filterCarriers, startDate, endDate } = {
      ...truckFilterFormData
    };

    let formData: any = {
      pickUpState: pickUpState?.map(({ value }) => value),
      deliveryState: deliveryState?.map(({ value }) => value),
      equipmentType: equipmentType?.map(({ value }) => value),
      filterCarriers: filterCarriers?.value
    };

    if (isDateRange) {
      formData = {
        ...formData,
        timeStart: startDate ? dayjs(startDate).format("YYYY-MM-DDTHH:mm:ss") : null,
        timeEnd: endDate ? dayjs(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss") : null
      };
    } else {
      formData = {
        ...formData,
        timeStart: startDate ? dayjs(startDate).format("YYYY-MM-DDTHH:mm:ss") : null,
        timeEnd: startDate ? dayjs(startDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss") : null
      };
    }
    setIsScroll(false);
    resetPagination();
    setFilter((prev) => ({
      orderBy: prev.orderBy,
      ...(formData.pickUpState && { pickUpState: formData.pickUpState }),
      ...(formData.deliveryState && { deliveryState: formData.deliveryState }),
      ...(formData.equipmentType && { equipmentType: formData.equipmentType }),
      ...(formData.filterCarriers && { haulpayVerified: formData.filterCarriers === "verified" }),
      timeStart: formData.timeStart,
      timeEnd: formData.timeEnd
    }));
    setPrevFilters(truckFilterFormData);
    if (!isDesktop) toggleFilterModal();
  };

  return (
    <form className={styles.truckFilterForm} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.fields}>
        <AutoComplete
          name="pickUpState"
          label={
            <div className={styles.pickUpStateLabel}>
              <span>&nbsp;&nbsp;Pick Up State(s)</span>
              <button type="button" className="text-primary" onClick={() => getCurrentState()}>
                Current State
              </button>
            </div>
          }
          placeholder="Any"
          options={REGION_DATA}
          control={control}
          isMulti
        />
        <AutoComplete
          name="deliveryState"
          label={isDesktop ? "Delivery State(s)" : <span className="pl-2">Delivery State(s)</span>}
          placeholder="Any"
          options={REGION_DATA}
          control={control}
          isMulti
        />
        <AutoComplete
          name="equipmentType"
          label="&nbsp;&nbsp;Equipment Type"
          placeholder="Any"
          options={EQUIPMENT_TYPES}
          control={control}
          isMulti
        />
        <Controller
          name="filterCarriers"
          control={control}
          render={({ field: { value = null, onChange } }) => (
            <Select
              value={value}
              options={FILTER_CARRIER_LIST}
              onChange={onChange}
              label="&nbsp;&nbsp;Filter Carriers"
              placeholder="Any"
            />
          )}
        />

        <Controller
          name="startDate"
          control={control}
          render={({ field: { value = undefined, onChange } }) => (
            <DatePicker
              value={value}
              onChange={onChange}
              label={
                <div className={styles.startDateLabel}>
                  <span>{isDateRange ? "Start" : " Date Available"}</span>
                  {isDesktop ? (
                    <button
                      type="button"
                      className={styles.dateRangeButton}
                      onClick={() => setIsDateRange((prev) => !prev)}>
                      {isDateRange ? "By Avail. Date" : "By Date Range"}
                    </button>
                  ) : null}
                </div>
              }
              labelClass="pl-2"
            />
          )}
        />

        {isDateRange && (
          <Controller
            name="endDate"
            control={control}
            render={({ field: { value = undefined, onChange } }) => (
              <DatePicker value={value} onChange={onChange} label="&nbsp;&nbsp;End" />
            )}
          />
        )}

        {!isDesktop ? (
          <div className="flex justify-between">
            <button
              type="button"
              className={styles.dateRangeButton}
              onClick={() => setIsDateRange((prev) => !prev)}>
              {isDateRange ? "By Avail. Date" : "By Date Range"}
            </button>

            <button
              type="button"
              className={clsx(styles.dateRangeButton, "!pl-0 !pr-2 desktop:!hidden")}
              onClick={onReverseSearch}>
              Reverse Search
            </button>
          </div>
        ) : null}
      </div>

      <div className={styles.footer}>
        <Button
          variant="outline"
          className={clsx(styles.button, "mobile:!hidden")}
          onClick={onReverseSearch}>
          Reverse
        </Button>
        <Button type="submit" className={styles.button} loading={isLoading}>
          Search
        </Button>
        {isSubmitted && (
          <Button
            onClick={() => {
              resetPagination();
              setFilter({
                orderBy: "-time_posted"
              });
              reset({
                pickUpState: [],
                deliveryState: [],
                equipmentType: [],
                startDate: "",
                endDate: ""
              });
            }}
            variant="outline"
            className={"flex-auto h-14 !text-red-600 !border-red-600"}>
            <Icon path={mdiClose} size={1} />
          </Button>
        )}
      </div>
    </form>
  );
}
